var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { buildCommerceImageSrcSet, identifyOfferStore, isCommerceItemImage, isCompleteCommerceItem, } from '@themaven-net/commerce-shared';
import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { buildButtonDetails, renderCommerceCta, renderCommerceLink } from '../utility.js';
import BaseOfferAwareComponent from './baseOfferAwareComponent.js';
import { ifDefined } from 'lit/directives/if-defined.js';
export default class BaseCommerceInfoblock extends BaseOfferAwareComponent {
    constructor() {
        super(...arguments);
        this.useGallery = false;
        this.currentImage = 0;
        /**
         * The function used to render a link, replace this to alter output
         * @see renderCommerceLink
         */
        this.renderLink = renderCommerceLink;
        /**
         * The function used to render a CTA button
         * @see renderCommerceCta
         */
        this.renderCta = renderCommerceCta;
        /**
         * Render an individual offer
         *
         * @returns TemplateResult for the offer.
         */
        this.renderOffer = (offer) => {
            if (offer === undefined) {
                return html `<div class="m-product-offers--offer m-product-offers--empty>No offer selected</div>`;
            }
            return this.showPrice ? this.renderOfferWithPrice(offer) : this.renderOfferWithoutPrice(offer);
        };
    }
    /**
     * Template method that returns the preferred merchants for the environment
     *
     * Override this to implemement per-environment logic
     */
    get defaultButtonText() {
        return 'Buy Now';
    }
    /**
     * Template method that returns the desired pre-item image load placeholder
     *
     * override this to implement per-environment value
     */
    get placeholderImage() {
        return 'https://youShouldOverrideMe.com/placeholder.gif';
    }
    /** @deprecated in favor of per button lables */
    get showPrice() {
        // If attribute is present, respect it whether true or false; otherwise respect component config
        if (this.showPriceAttr !== undefined) {
            return this.showPriceAttr;
        }
        return this.config?.showPrice === true;
    }
    get itemTitle() {
        return this.config?.title ?? this.item?.title ?? '';
    }
    render() {
        if (!isCompleteCommerceItem(this.config?.item)) {
            this.selectOffers(this.item);
        }
        return html `
            ${this.renderImages()}
            <div class="m-product--details noskim">
                ${this.renderTitle()}
                ${this.renderDescription()}
                ${this.renderOffers()}</div>
            </div>
        `;
    }
    renderTitle() {
        const offer = this.offerSelection?.primaryOffer;
        if (offer === undefined) {
            return html ``;
        }
        return html ` <span class="m-product--title">
            ${this.renderLink(this.itemTitle, offer.url, isCompleteCommerceItem(this.config?.item) ? this.config?.item : undefined, this.config?.options, false, {}, 'm-product--title-link')}
        </span>`;
    }
    /**
     * Render all images for the product
     *
     * @returns a TemplateResult that contains the image(s) for this infoblock
     */
    renderImages() {
        if (this.images.length === 0) {
            return null;
        }
        let images;
        // Feature flag for later use
        if (this.useGallery === false) {
            images = [this.renderImage(this.images[0], 0)];
        }
        else {
            images = this.images.map(this.renderImage.bind(this));
        }
        return html `<div class="m-product--images noskim">${images}</div> `;
    }
    /**
     * Render markup individual image definition
     *
     * @param image the image to render
     * @param index the key of the image in the array (to be used for paging through mulitple images)
     * @returns
     */
    renderImage(image, index) {
        const offer = this.offerSelection?.primaryOffer;
        if (!isCommerceItemImage(image) || offer === undefined) {
            return html ``;
        }
        return html `
            ${this.renderLink(this.renderImagePictureTag(image), offer, this.item, this.config?.options, this.currentImage !== index)}
        `;
    }
    renderImagePictureTag(image) {
        if (image.sizes === undefined || image.sizes.length === 0) {
            return html `<span class="m-product--image m-product--image-empty`;
        }
        const srcset = buildCommerceImageSrcSet(image);
        // Use the largest image as the "src" which will not be immediately displayed
        // due to the "srcset" placeholder but will be exposed for crawlers
        const seoSrc = image.sizes[image.sizes.length - 1].src;
        return html `<picture class="is-waiting-to-load">
            <source
                data-srcset="${srcset}"
                sizes="(min-width: 1240px) 267px, (min-width: 675px) 235px, calc(100vw - 40px)"
            />
            <img
                class="m-product--image"
                alt="${ifDefined(image.alt)}"
                src="${seoSrc}"
                srcset="${this.placeholderImage}"
                loading="lazy"
            />
        </picture>`;
    }
    /**
     * Return markup for the product description if available
     *
     * @returns TemplateResult containing the description, or null
     */
    renderDescription() {
        if (this.config?.description === undefined) {
            return html ``;
        }
        return html `<div class="m-product--description">${this.config.description}</div>`;
    }
    /**
     * Render the correct offer markup for this infoblock
     *
     * Depending on configuration it will render all offers, or just the selected
     *
     * @returns Array of template results for one or more offers
     */
    renderOffers() {
        const selected = this.offerSelection?.selectedOffers ?? [];
        return html `<div class="m-product-offers">${selected.map((offer) => this.renderOffer(offer))}</div>`;
    }
    /**
     * Render an individual offer with the "show price" option enabled
     *
     * @todo this will probably go away with the token replacement method
     * @returns TemplateResult for the offer.
     */
    renderOfferWithPrice(offer) {
        let saved = '';
        if (offer.saved !== undefined) {
            saved = html `<span class="m-product-offers--saved">You save: ${offer.saved}</span> `;
        }
        const vendorName = identifyOfferStore(offer);
        // // external without a defined store name, render the "without price" version.
        if (vendorName === undefined) {
            return this.renderOfferWithoutPrice(offer);
        }
        return html `
            <div class="m-product-offers--offer">
                <div class="m-product-offers--offer-details">
                    ${this.renderBuyButton(offer)}
                    <div class="m-product-offers--price-container">
                        <span class="m-product-offers--price">${offer.price} from ${vendorName}</span>
                        ${saved}
                    </div>
                </div>
                ${this.renderFormattedDate(offer)}
            </div>
        `;
    }
    /**
     * Render an individual offer without the "show price" option enabled (just a button to check price)
     *
     * @returns TemplateResult for the offer.
     */
    renderOfferWithoutPrice(offer) {
        return html `
            <div class="m-product-offers--offer">
                <div class="m-product-offers--offer-details">${this.renderBuyButton(offer)}</div>
            </div>
        `;
    }
    /**
     * Render the buy button with the specified url and button text
     *
     * @returns TemplateResult for the button.
     */
    renderBuyButton(offer) {
        const buttonDetails = buildButtonDetails(offer, this.config?.options ?? {}, this.defaultButtonText);
        return html `<div class="m-product-offers--buy-container">
            ${this.renderCta(buttonDetails, this.item, this.config?.options)}
        </div>`;
    }
    /**
     * Return a template result if the offer has "last pulled on" date info
     *
     * @param offer the object to check for a pullDate
     * @returns Template
     */
    renderFormattedDate(offer) {
        if (offer.pullDate === undefined || offer.pullDate === false) {
            return null;
        }
        const date = new Date(offer.pullDate);
        return html `<div class="m-product-offers--refresh-details">Price updated ${date.toISOString()}</div>`;
    }
}
__decorate([
    property(),
    __metadata("design:type", Object)
], BaseCommerceInfoblock.prototype, "useGallery", void 0);
__decorate([
    property({ type: Object, attribute: 'show-price' }),
    __metadata("design:type", Boolean)
], BaseCommerceInfoblock.prototype, "showPriceAttr", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], BaseCommerceInfoblock.prototype, "currentImage", void 0);
//# sourceMappingURL=baseCommerceInfoblock.js.map